import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { cx, css, etiColors, keyframes } from '@eti/styles';
import { usePrevious } from '@eti/utils';
import { usePersuasionModal } from '../../../common/context/PersuasionModalContext';
import { createValidation } from '../../../common/utils/validation';
import { generateReadMore } from '../../../common/utils/readMore';
import { generateModal } from '../../../common/utils/modal';
import Product from '../../../common/components/Product';
import { TRACKING_VALUES, useProductSnowplowTracking } from '../../../custom-hooks/useProductSnowplowTracking';
import { useDidUpdateEffect } from '../../../custom-hooks/useDidUpdateEffect';
import ManulifeToggleBar from './ManulifeToggleBar';
import ManulifeContent from './ManulifeContent';
import ManulifeLogo from './ManulifeLogo';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const persuasionModalName = name => `${name}Persuasion`; // TODO: Remove?
const confirmationModalName = name => `${name}Confirmation`;
const highlight = keyframes`
  from { color: ${etiColors.confirmation.solid}; }
  to { color: ${etiColors.black}; }
`;
const productBoxStyles = isSelected => isSelected ? css`
        & [data-productimage] {
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
        }

        & [data-productbox] {
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
        }
      ` : null;
const highlightPrice = css`
  .product-price {
    animation: 1s ${highlight} 1s forwards;
    color: ${etiColors.confirmation.solid};
  }
`;
const ManulifeInsurance = _ref => {
  let {
    additionalInformation,
    className,
    confirmation,
    confirmationContent,
    dateFormat,
    errors,
    form,
    hasLogo,
    hasPriceTravelerError,
    isNeutral,
    isProvinceSelected,
    isSelectable,
    isSelected,
    isTravelersValid,
    notSelectedLabel,
    onFetchPrice,
    persuasion,
    persuasionContent,
    priceRaw,
    productId,
    productName,
    productPosition,
    provinces,
    readMore = {},
    readMoreContent,
    shouldShowImage = true,
    shouldShowMandatoryChoiceText,
    text,
    title,
    travelers,
    updateIsSelected,
    updateTotalPrice,
    upSell
  } = _ref;
  const previousPrice = usePrevious(priceRaw);
  const {
    isOpen,
    onConfirm,
    onDecline,
    setIsOpen,
    setOnConfirm,
    setOnDecline
  } = usePersuasionModal();
  const rootElement = useRef(null);
  const {
    sendAddToCartEvent,
    sendSelectEvent
  } = useProductSnowplowTracking(productName, {
    productPosition,
    rootElement
  });
  useEffect(() => {
    if (priceRaw !== previousPrice && isProvinceSelected) {
      if (priceRaw === null && !hasPriceTravelerError && isTravelersValid) {
        updateIsSelected(false);
      }
      updateTotalPrice(priceRaw);
    }
  }, [hasPriceTravelerError, isProvinceSelected, isTravelersValid, previousPrice, priceRaw, updateIsSelected, updateTotalPrice]);
  useDidUpdateEffect(() => {
    if (isSelected && !isProvinceSelected) {
      sendSelectEvent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelected]);
  useEffect(() => {
    const selectNoneEvent = event => {
      event.stopPropagation();
      updateIsSelected(false);
      sendAddToCartEvent({
        addToCart: false,
        value: TRACKING_VALUES.RADIO_BUTTON
      });
    };
    let noButton;
    if (rootElement.current) {
      noButton = rootElement.current.querySelector(`#${productName}_false`);
      if (noButton) {
        noButton.addEventListener('click', selectNoneEvent);
      }
    }
    return () => {
      if (noButton) {
        noButton.removeEventListener('click', selectNoneEvent);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootElement.current]);
  return /*#__PURE__*/_jsxs("div", {
    ref: rootElement,
    children: [/*#__PURE__*/_jsx(Product, {
      additionalInformation: additionalInformation,
      buttons: /*#__PURE__*/_jsx(ManulifeToggleBar, {
        confirmation: confirmation,
        confirmationModalName: confirmationModalName(productName),
        "data-testid": `${productName}-toggleBar`,
        form: form,
        isNeutral: isNeutral,
        isSelected: isSelected,
        persuasion: persuasion,
        priceRaw: priceRaw,
        productName: productName,
        setPersuasionIsOpen: setIsOpen,
        setPersuasionOnConfirm: setOnConfirm,
        setPersuasionOnDecline: setOnDecline
      }),
      className: cx('manulife', productName, className, highlightPrice, productBoxStyles(isSelected)),
      confirmation: generateModal({
        ...confirmation
      }, confirmationContent, confirmationModalName(productName)),
      description: text,
      hasLogo: hasLogo,
      isPersuasionOpen: isOpen,
      isSelectable: isSelectable,
      isSelected: isSelected,
      notSelectedLabel: notSelectedLabel,
      onPersuasionConfirm: onConfirm,
      onPersuasionDecline: onDecline,
      persuasion: generateModal({
        ...persuasion
      }, persuasionContent, persuasionModalName(productName)),
      priceRaw: priceRaw,
      productId: productId,
      productName: productName,
      readMore: generateReadMore(readMore, readMoreContent),
      selectionAdditionalContent: shouldShowImage ? /*#__PURE__*/_jsx(ManulifeLogo, {}) : null,
      shouldShowImage: shouldShowImage,
      shouldShowMandatoryChoiceText: shouldShowMandatoryChoiceText,
      title: title,
      upSell: upSell,
      validation: createValidation(errors)
    }), /*#__PURE__*/_jsx(ManulifeContent, {
      className: className,
      dateFormat: dateFormat,
      form: form,
      isActive: isSelected,
      onFetchPrice: onFetchPrice,
      productName: productName,
      provinces: provinces,
      shouldShowTravelers: isProvinceSelected,
      travelers: travelers
    })]
  });
};
ManulifeInsurance.propTypes = {
  additionalInformation: PropTypes.string,
  className: PropTypes.string,
  confirmation: PropTypes.shape({
    isEnabled: PropTypes.bool
  }),
  confirmationContent: PropTypes.string,
  dateFormat: PropTypes.string,
  errors: PropTypes.arrayOf(PropTypes.object),
  form: PropTypes.string,
  hasLogo: PropTypes.bool,
  hasPriceTravelerError: PropTypes.bool,
  highlightedInformation: PropTypes.shape({
    isEnabled: PropTypes.bool
  }),
  isNeutral: PropTypes.bool,
  isProvinceSelected: PropTypes.bool,
  isSelectable: PropTypes.bool,
  isSelected: PropTypes.bool,
  isTravelersValid: PropTypes.bool,
  notSelectedLabel: PropTypes.string,
  onFetchPrice: PropTypes.func.isRequired,
  persuasion: PropTypes.shape({
    isEnabled: PropTypes.bool
  }),
  persuasionContent: PropTypes.string,
  priceRaw: PropTypes.number,
  productId: PropTypes.number,
  productName: PropTypes.string,
  productPosition: PropTypes.number,
  provinces: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string
  })),
  readMore: PropTypes.shape({
    isEnabled: PropTypes.bool
  }),
  readMoreContent: PropTypes.string,
  shouldShowImage: PropTypes.bool,
  shouldShowMandatoryChoiceText: PropTypes.bool,
  text: PropTypes.string,
  title: PropTypes.string,
  travelers: PropTypes.arrayOf(PropTypes.shape({
    ageType: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string
  })),
  updateIsSelected: PropTypes.func,
  updateTotalPrice: PropTypes.func,
  upSell: PropTypes.string
};
export default ManulifeInsurance;
//# sourceMappingURL=ManulifeInsurance.js.map